import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Sparkles, X, Send, Loader2, BookOpen } from 'lucide-react';
import api from '../lib/api';

const TutorialChat = () => {
  const { theme } = useTheme();
  const { t, lang } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: t('chat_greeting') }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  // Reset the greeting whenever the language changes so the chat reflects the active locale
  // (only if there are no user messages yet — keep ongoing conversations intact).
  useEffect(() => {
    setMessages((m) => (m.length <= 1 ? [{ role: 'assistant', content: t('chat_greeting') }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const suggested = [t('chat_sug_1'), t('chat_sug_2'), t('chat_sug_3'), t('chat_sug_4')];

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setSending(true);
    try {
      const { data } = await api.post('/tutorial/chat', { question: q, context: location.pathname, lang });
      setMessages((m) => [...m, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: t('chat_error') }]);
    } finally {
      setSending(false);
    }
  };

  const openTour = () => {
    window.dispatchEvent(new Event('teko-tour-open'));
    setOpen(false);
  };

  return (
    <>
      {/* Floating button — positioned above the Emergent badge */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 sm:bottom-20 sm:right-6 z-[110] flex items-center gap-2 px-4 py-3 rounded-full shadow-xl transition-transform hover:scale-105"
        style={{
          background: theme.accent,
          color: theme.bg,
          boxShadow: `0 10px 30px ${theme.accent}50`,
          display: open ? 'none' : 'flex',
        }}
        data-testid="tutorial-chat-button"
        aria-label={t('chat_open_aria')}
      >
        <Sparkles size={16} />
        <span className="text-sm font-medium hidden sm:inline">{t('chat_title')}</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 z-[90] w-full sm:w-[380px] rounded-t-3xl sm:rounded-3xl flex flex-col"
          style={{
            background: theme.card,
            border: `1px solid ${theme.accent}40`,
            boxShadow: `0 24px 60px ${theme.accent}30`,
            height: 'min(560px, 80vh)',
          }}
          data-testid="tutorial-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 rounded-t-3xl" style={{ borderBottom: `1px solid ${theme.border}` }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${theme.accent}25` }}>
                <Sparkles size={14} style={{ color: theme.accent }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{t('chat_title')}</p>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{t('chat_subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={openTour} className="text-[10px] px-2 py-1 rounded hover:opacity-80 transition flex items-center gap-1" style={{ color: theme.textMuted }} data-testid="tutorial-chat-open-tour">
                <BookOpen size={11} /> {t('chat_tour_btn')}
              </button>
              <button onClick={() => setOpen(false)} className="p-1" style={{ color: theme.textMuted }} aria-label={t('chat_close_aria')}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="tutorial-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed"
                  style={{
                    background: m.role === 'user' ? theme.primary : theme.elevated,
                    color: m.role === 'user' ? '#fff' : theme.textPrimary,
                    border: m.role === 'user' ? 'none' : `1px solid ${theme.border}`,
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3.5 py-2 text-sm flex items-center gap-2"
                  style={{ background: theme.elevated, color: theme.textMuted, border: `1px solid ${theme.border}` }}>
                  <Loader2 size={12} className="animate-spin" /> {t('thinking')}
                </div>
              </div>
            )}
          </div>

          {/* Suggestions (only first interaction) */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {suggested.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full transition-colors hover:opacity-80"
                  style={{
                    background: theme.elevated,
                    color: theme.textSecondary,
                    border: `1px solid ${theme.border}`,
                  }}
                  data-testid={`tutorial-chat-suggested-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-3"
            style={{ borderTop: `1px solid ${theme.border}` }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chat_placeholder')}
              disabled={sending}
              className="flex-1 bg-transparent outline-none text-sm px-2"
              style={{ color: theme.textPrimary }}
              data-testid="tutorial-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="p-2 rounded-full transition-opacity disabled:opacity-40"
              style={{ background: theme.accent, color: theme.bg }}
              data-testid="tutorial-chat-send"
              aria-label={t('chat_send_aria')}
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default TutorialChat;
